import { formatMicros } from './money.js'

/**
 * What `consent.v2` fills in from the organization. The organization can
 * change these later, so each acceptance records them (see
 * {@link consentTermsPayload}) and the exact text a signer agreed to can be
 * rebuilt from the version and that record (docs/consent-and-paper-copies.md).
 */
export interface ConsentTerms {
  organization: string
  /** The organization's reply-to address: where signers ask for paper, withdraw, or update their contact details. */
  contactEmail: string
  /** What the organization charges for a paper copy; 0 is free. */
  paperCopyFeeMicros: number
}

/** "Paper copies are free." or "Acme charges $5.00 for each paper copy." */
export function paperCopyFeeSentence(organization: string, feeMicros: number): string {
  return feeMicros === 0
    ? 'Paper copies are free.'
    : `${organization} charges ${formatMicros(feeMicros)} for each paper copy.`
}

/** "free" or "$5.00", as a settings page or a change log shows the fee. */
export function paperCopyFeeLabel(feeMicros: number): string {
  return feeMicros === 0 ? 'free' : formatMicros(feeMicros)
}

/**
 * The electronic records consent (`consent.v2`): the ESIGN consumer
 * disclosure, with who to contact, the paper-copy fee, and what the consent
 * covers. A wording change is a new version.
 */
export function consentText({ organization, contactEmail, paperCopyFeeMicros }: ConsentTerms): string {
  return `By agreeing, you consent to receive and sign records from ${organization} electronically, under the U.S. ESIGN Act and the Uniform Electronic Transactions Act (UETA). A signature you draw or type has the same legal effect as a handwritten one.

What this covers: the documents in this request, and other records ${organization} sends you to view or sign electronically through PixelSign, until you withdraw your consent.

What you need: a current web browser that can open PDF documents, and a way to download or print them so you can keep a copy.

Paper copies: you have the right to get these records on paper. To deal on paper instead, choose “I don’t agree”. After you agree, you can still ask for a paper copy at any time: choose “Request a paper copy” on the signing page, or email ${organization} at ${contactEmail}. ${paperCopyFeeSentence(organization, paperCopyFeeMicros)}

Withdrawing your consent: you can withdraw at any time, at no charge, by emailing ${organization} at ${contactEmail}. Withdrawing doesn’t change records you have already signed electronically, and ${organization} will then provide records to you on paper.

Your contact details: to change the email address ${organization} uses to reach you, email ${organization} at ${contactEmail}.

If you don’t agree, you can’t view or sign these documents electronically. ${organization} is told, and you can contact them to sign another way.`
}

/** The filled-in terms as an audit payload, alongside the version. */
export function consentTermsPayload(terms: ConsentTerms): { organization: string; contactEmail: string; paperCopyFee: string } {
  return {
    organization: terms.organization,
    contactEmail: terms.contactEmail,
    paperCopyFee: paperCopyFeeLabel(terms.paperCopyFeeMicros),
  }
}
